import { MIN_SCALE, MAX_SCALE, MIN_SHAPE_DIMENSION } from './constants.js';

export const TOUCH_DRAG_THRESHOLD = 6;

export function touchPair(points) {
  const [a, b] = points;
  return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2, distance: Math.hypot(b.x - a.x, b.y - a.y) };
}

export function pinchTransform(view, start, current, min = MIN_SCALE, max = MAX_SCALE) {
  const scale = Math.max(min, Math.min(max, (view.scale * current.distance) / Math.max(1, start.distance)));
  return {
    scale,
    x: current.x - ((start.x - view.x) * scale) / view.scale,
    y: current.y - ((start.y - view.y) * scale) / view.scale
  };
}

// Own canvas touches before Konva's mouse-like touch handlers or Transformer can
// change anything. Controls are outside this container and keep native gestures.
export const TouchGestureMethods = {
  _connectCanvasTouch() {
    this._disconnectCanvasTouch();
    this._touchSurface = this.containerTarget;
    this._canvasTouchHandler = (event) => this._routeCanvasTouch(event);
    for (const name of ['touchstart', 'touchmove', 'touchend', 'touchcancel']) {
      this._touchSurface.addEventListener(name, this._canvasTouchHandler, { capture: true, passive: false });
    }
  },

  _disconnectCanvasTouch() {
    if (!this._touchSurface) return;
    for (const name of ['touchstart', 'touchmove', 'touchend', 'touchcancel']) {
      this._touchSurface.removeEventListener(name, this._canvasTouchHandler, true);
    }
    this._touchSurface = null;
  },

  _canvasView() {
    return { x: this._stage.x(), y: this._stage.y(), scale: this._stage.scaleX() };
  },

  _beginCanvasGesture() {
    this._gestureSnapshot = {
      canvas: JSON.stringify(this._toJson()),
      position: [this.positionZoomValue, this.positionPosXValue, this.positionPosYValue],
      panel: this._mobilePanel,
      collapsed: this._shapePopoverCollapsed,
      placement: this._areaEffectPlacementPoint && { ...this._areaEffectPlacementPoint }
    };
  },

  _rollbackCanvasGesture() {
    this._gridCalibrationPointerDown = false;
    const snapshot = this._gestureSnapshot;
    const transforming = this._shapeTransforming;
    this._effectRotating = false;
    this._shapeDragging = false;
    this._shapeMoved = false;
    this._characterMovePointer = null;
    this._cycleShapeOnClick = false;
    this._shapeTransforming = false;
    this._painting = false;
    this._clearCharacterPreview?.();
    this._dragging = false;
    this._positionDragging = false;
    this._lastLine = null;
    this._rectPreviewShape?.destroy();
    this._rectPreviewShape = null;
    this._rectStart = null;
    this._touchResize = null;
    // stopTransform emits transformend synchronously; suppress its save.
    this._cancellingGesture = true;
    this._shapeTransformer?.stopTransform();
    this._cancellingGesture = false;
    if (snapshot) {
      if (transforming || JSON.stringify(this._toJson()) !== snapshot.canvas)
        this._restoreFromJson(JSON.parse(snapshot.canvas));
      if (this._mobileEditor) {
        this._mobilePanel = snapshot.panel;
        this._shapePopoverCollapsed = snapshot.collapsed;
        this._restoreToolbarVisibility();
      }
      if (this._positionManagerActive) {
        [this.positionZoomValue, this.positionPosXValue, this.positionPosYValue] = snapshot.position;
        this._applyPositionManagerTransform();
      }
      this._areaEffectPlacementPoint = snapshot.placement;
      this._clearAreaEffectPreview();
      if (snapshot.placement) this._previewAreaEffect(snapshot.placement);
    }
    this._cursorLayer?.batchDraw();
    this._syncMobileControls?.();
  },

  _cancelCanvasGesture() {
    if (!this._gestureSnapshot) return;
    this._rollbackCanvasGesture();
    this._gestureSnapshot = null;
    // Ignore the rest of an interrupted finger sequence, including its release.
    if (this._touchGesture) this._touchGesture.mode = 'cancelled';
    this._syncMobileControls?.();
  },

  _routeCanvasTouch(event) {
    if (!this._stage || this.readonlyValue) return;
    if (event.cancelable !== false) event.preventDefault();
    event.stopImmediatePropagation();
    this._usingTouch = true;
    const bounds = this.containerTarget.getBoundingClientRect();
    const touches = Array.from(event.touches).filter((touch) => this.containerTarget.contains(touch.target));
    const points = touches.map((touch) => ({ x: touch.clientX - bounds.left, y: touch.clientY - bounds.top }));
    this._stage.setPointersPositions({
      touches,
      changedTouches: Array.from(event.changedTouches || []).filter((touch) =>
        this.containerTarget.contains(touch.target)
      )
    });

    if (event.type === 'touchcancel') {
      this._cancelCanvasGesture();
      if (!points.length) this._touchGesture = null;
      return;
    }
    if (!this._touchGesture && event.type === 'touchstart') {
      this._beginCanvasGesture();
      this._touchGesture = { mode: 'edit', start: points[0], moved: false };
      if (points.length === 1) {
        if (this._tool === 'area-effect') this._previewAreaEffect();
        else if (!this._startTouchResize(points[0])) this._onPointerDown({ target: this._stage });
      }
      this._syncMobileControls?.();
    }
    const gesture = this._touchGesture;
    if (!gesture) return;
    if (gesture.mode === 'cancelled') {
      if (!points.length) this._touchGesture = null;
      return;
    }
    if (points.length >= 2) {
      if (gesture.mode !== 'navigate') {
        if (gesture.mode === 'edit') this._rollbackCanvasGesture();
        gesture.mode = 'navigate';
        gesture.pair = touchPair(points);
        gesture.view = this._canvasView();
      }
      if (event.type === 'touchmove') {
        const base = this._positionManagerActive ? this._positionBaseScale() : 1;
        const next = pinchTransform(gesture.view, gesture.pair, touchPair(points), MIN_SCALE * base, MAX_SCALE * base);
        if (this._positionManagerActive && this.workspaceValue) this._applyMonitorPinch(next);
        else if (this._positionManagerActive) {
          this.positionZoomValue = (next.scale / base - 1) * 100;
          this.positionPosXValue =
            ((next.x - (this._stage.width() - this._imgWidth * next.scale) / 2) / (this._imgWidth * next.scale)) * 100;
          this.positionPosYValue =
            ((next.y - (this._stage.height() - this._imgHeight * next.scale) / 2) / (this._imgHeight * next.scale)) *
            100;
          this._applyPositionManagerTransform();
        } else {
          this._currentScale = next.scale;
          this._stage.scale({ x: next.scale, y: next.scale });
          this._stage.position(next);
          this._stage.batchDraw();
          this._updateZoomDisplay();
        }
        gesture.navigated = true;
      }
      return;
    }
    if (gesture.mode === 'navigate') gesture.mode = 'blocked';
    if (!points.length) {
      if (gesture.mode === 'edit') {
        if (this._touchResize) this._commitShapeResize();
        // Wide touchscreens use the desktop layout, without a Place button.
        // Wait for release so a second finger can still cancel into navigation.
        else if (this._tool === 'area-effect' && !this._mobileEditor)
          this._placeAreaEffect(this._areaEffectPlacementPoint);
        else if (this._tool !== 'area-effect') this._onPointerUp();
      } else if (gesture.navigated && this._positionManagerActive) {
        this.savePosition();
      }
      this._gestureSnapshot = null;
      this._touchGesture = null;
      this._touchResize = null;
      this._syncMobileControls?.();
      this._rememberEditorState?.();
      return;
    }
    if (gesture.mode !== 'edit' || event.type !== 'touchmove') return;
    const point = points[0];
    gesture.moved ||= Math.hypot(point.x - gesture.start.x, point.y - gesture.start.y) >= TOUCH_DRAG_THRESHOLD;
    if (!gesture.moved) return;
    if (this._touchResize) this._moveTouchResize(point);
    else this._onPointerMove();
  },

  _startTouchResize(point) {
    if (this._tool !== 'select') return false;
    const shape = this._findShapeById(this._selectedShapeId);
    if (!shape || ['area-effect', 'character'].includes(shape.type)) return false;
    const node = shape.konvaShape;
    const view = this._canvasView();
    const corners = [
      [0, 0],
      [1, 0],
      [0, 1],
      [1, 1]
    ]
      .map(([x, y]) => ({
        x,
        y,
        distance: Math.hypot(
          point.x - (view.x + (node.x() - node.offsetX() + x * node.width()) * view.scale),
          point.y - (view.y + (node.y() - node.offsetY() + y * node.height()) * view.scale)
        )
      }))
      .sort((a, b) => a.distance - b.distance);
    const corner = corners[0];
    if (corner.distance > 22) return false;
    this._finishShapeAnimations?.();
    this._touchResize = {
      node,
      corner,
      opposite: { x: node.x() + (1 - corner.x) * node.width(), y: node.y() + (1 - corner.y) * node.height() }
    };
    return true;
  },

  _moveTouchResize(point) {
    const { node, corner, opposite } = this._touchResize;
    const view = this._canvasView();
    const x = (point.x - view.x) / view.scale;
    const y = (point.y - view.y) / view.scale;
    const width = Math.max(MIN_SHAPE_DIMENSION, (x - opposite.x) * (corner.x ? 1 : -1));
    const height = Math.max(MIN_SHAPE_DIMENSION, (y - opposite.y) * (corner.y ? 1 : -1));
    node.setAttrs({
      x: corner.x ? opposite.x : opposite.x - width,
      y: corner.y ? opposite.y : opposite.y - height,
      width,
      height
    });
    this._shapeTransformer.forceUpdate();
    this._shapesLayerAbove.batchDraw();
  }
};
